import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { durations, easings } from "@samklang/design-tokens";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/use-reduced-motion";
import { Text } from "./text";

/**
 * The optional members spell out `| undefined` because the repo runs with
 * `exactOptionalPropertyTypes`: the shell reads them off a screen's published
 * chrome, where a screen with no back navigation leaves them undefined.
 */
export type AppTopBarProps = {
  readonly title: string;
  readonly onBack?: (() => void) | undefined;
  readonly backLabel?: string | undefined;
  readonly action?: ReactNode | undefined;
};

/**
 * The scroll position at which the bar stops being transparent, spec 11.3.
 *
 * Deliberately low: the background has to answer the first flick of a finger,
 * otherwise the lit state reads as lag rather than as a response.
 */
export const APP_TOP_BAR_SCROLL_THRESHOLD = 8;

function cubicBezier(easing: readonly number[]): string {
  return `cubic-bezier(${easing.join(", ")})`;
}

function documentScrollTop(): number {
  return document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
}

/**
 * True once the document is scrolled past the threshold.
 *
 * The raw position lives in a ref and React state is written only when the
 * threshold is crossed, so scrolling a long page costs one render rather than
 * one render per scroll event.
 */
function useScrolledPastThreshold(): boolean {
  const [scrolled, setScrolled] = useState(false);
  const scrolledRef = useRef(false);

  useEffect(() => {
    const read = () => {
      const next = documentScrollTop() > APP_TOP_BAR_SCROLL_THRESHOLD;
      if (next === scrolledRef.current) return;
      scrolledRef.current = next;
      setScrolled(next);
    };

    read();
    window.addEventListener("scroll", read, { passive: true });
    return () => window.removeEventListener("scroll", read);
  }, []);

  return scrolled;
}

/**
 * The one top bar in the app, spec 11.3.
 *
 * Transparent at the top of a page and lit with a surface background once the
 * document is scrolled. That is the reason the app has a single scroll
 * container: this bar observes one global scroll position instead of guessing
 * which of five mounted panes moved.
 *
 * `sticky` and not `fixed`, because a sticky bar takes part in the flow and so
 * needs no compensating top margin anywhere in the content.
 *
 * The lit state is a CSS transition on `background-color`, `backdrop-filter`
 * and `border-color`: those are colour tokens held in CSS variables, which a
 * JavaScript animation would have to resolve first, and handing them to the
 * compositor is cheaper than driving them from a scroll handler. So the
 * preference comes from the shared `useReducedMotion` hook, which reads the
 * media query per mount, rather than from `motion/react`, which caches it
 * globally on first use.
 *
 * The title is deliberately not an `h1`. Every screen keeps its own heading in
 * its content, so heading order stays intact and the anchors that read a screen
 * by its heading keep working.
 *
 * Which leaves the bar's title as a second copy of a heading the reader can
 * already see, so it fades in with the background: at the top the screen's own
 * heading is the title, and once that heading has scrolled away the bar carries
 * it. The text stays in the DOM either way, because the back control is named
 * after it when a screen gives no label of its own.
 */
export function AppTopBar({ title, onBack, backLabel, action }: AppTopBarProps) {
  const scrolled = useScrolledPastThreshold();
  const reduce = useReducedMotion();
  const transition = {
    transitionDuration: `${reduce ? 0 : durations.fast}s`,
    transitionTimingFunction: cubicBezier(easings.standard),
  };

  return (
    <header
      data-app-top-bar=""
      data-scrolled={scrolled ? "true" : "false"}
      className={cn(
        "sticky top-0 z-30 w-full border-b",
        scrolled
          ? "border-border/60 bg-background/95 backdrop-blur-md"
          : "border-transparent bg-transparent"
      )}
      style={{
        // The bar owns the top inset now, so no screen has to reserve room for
        // the status bar or the notch.
        paddingTop: "max(var(--app-shell-top-inset), env(safe-area-inset-top))",
        transitionProperty: "background-color, backdrop-filter, border-color",
        ...transition,
      }}
    >
      <div className="mx-auto flex w-full max-w-lg items-center gap-1.5 px-5 sm:px-6">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel ?? title}
            className={cn(
              "-ml-2 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center",
              "rounded-full text-foreground-muted transition-colors",
              "hover:bg-surface-muted hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            )}
          >
            <svg
              aria-hidden
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </button>
        ) : null}

        <div className="flex min-h-11 min-w-0 flex-1 items-center">
          <Text
            variant="heading"
            as="p"
            className={cn(
              "truncate text-base",
              scrolled ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionProperty: "opacity", ...transition }}
          >
            {title}
          </Text>
        </div>

        {action ? <div className="flex shrink-0 items-center">{action}</div> : null}
      </div>
    </header>
  );
}

/**
 * What a screen tells the shared top bar about itself.
 *
 * Exactly the bar's own props, because a host forwards them unchanged: one
 * shape, so the two can never drift apart.
 */
export type ScreenChrome = AppTopBarProps;

type PublishScreenChrome = (chrome: ScreenChrome | null) => void;

const ScreenChromeContext = createContext<PublishScreenChrome | null>(null);

/**
 * The state behind one shared top bar: the chrome of whichever screen is
 * showing, and the publisher to hand to `ScreenChromeProvider`.
 *
 * Every host of the bar needs the same bail-out, so it lives here rather than
 * being written out again per host: a screen that unmounts publishes `null`, and
 * writing `null` over `null` is a state update that would re-render the host for
 * nothing.
 */
export function useScreenChromeHost(): {
  readonly chrome: ScreenChrome | null;
  readonly publish: PublishScreenChrome;
} {
  const [chrome, setChrome] = useState<ScreenChrome | null>(null);
  const publish = useCallback<PublishScreenChrome>((next) => {
    setChrome((current) => (next === null && current === null ? current : next));
  }, []);

  return { chrome, publish };
}

/**
 * Lets the shell collect the title and back navigation of whichever screen is
 * showing.
 *
 * The shell provides one publisher per route slot rather than one for the whole
 * app, and passes `null` for a slot that is not showing. Five tab screens stay
 * mounted at any time and only the visible one may speak for the top bar.
 */
export function ScreenChromeProvider({
  publish,
  children,
}: {
  publish: PublishScreenChrome | null;
  children: ReactNode;
}) {
  return (
    <ScreenChromeContext.Provider value={publish}>
      {children}
    </ScreenChromeContext.Provider>
  );
}

/**
 * Publishes a screen's own title and back navigation to the shared top bar.
 *
 * Used by `Screen`, so a screen declares its chrome as props rather than
 * rendering a second bar of its own.
 *
 * The callback and the action node are held in a ref rather than watched as
 * dependencies, and this is not a micro optimisation, it is what keeps the app
 * from spinning. Every screen passes its back navigation inline, as
 * `onTopBarBack={() => void navigate(...)}`, which is a new function identity on
 * every render. Publishing writes shell state, the shell re-renders the screen,
 * the screen hands over a new identity, and the effect runs again: a render loop
 * on every back-navigable screen in the app. Only the title and the label decide
 * when the bar has something new to say, because only they are values.
 *
 * The cost of that: an `action` node is read once, when the title or the label
 * changes. A screen that swaps its action without changing its title would keep
 * showing the first one. No screen does today, and the guard test named after
 * this paragraph fails the day one tries, which is where the limitation should
 * be discovered rather than in the bar.
 */
export function usePublishScreenChrome(chrome: ScreenChrome | null): void {
  const publish = useContext(ScreenChromeContext);
  const title = chrome?.title;
  const backLabel = chrome?.backLabel;
  const hasBack = chrome?.onBack !== undefined;

  const latest = useRef(chrome);
  latest.current = chrome;

  // Stable for the life of the hook, so the bar keeps working after a screen
  // re-renders with a fresh closure. It reads whatever the last render passed.
  const back = useRef<() => void>(() => {
    latest.current?.onBack?.();
  });

  useEffect(() => {
    if (!publish || title === undefined) return;
    publish({
      title,
      backLabel,
      onBack: hasBack ? back.current : undefined,
      action: latest.current?.action,
    });
    return () => publish(null);
  }, [publish, title, backLabel, hasBack]);
}
